/**
 * Шифрование резервной копии.
 *
 * Копия — единственное место, где данные покидают устройство: файл уезжает в
 * Google Диск, Яндекс.Диск, в почту. А в нём открытым текстом лежат лекарства,
 * дозы и заметки о самочувствии. Облако можно взломать, случайно расшарить,
 * потерять вместе с аккаунтом — и это самый вероятный путь утечки во всём
 * приложении.
 *
 * Поэтому копию можно закрыть паролем. Шифрование целиком локальное: ключ
 * выводится из пароля на устройстве, наружу уходит только шифротекст.
 *
 * ## Как устроено
 *
 * PBKDF2-SHA256 выводит ключ из пароля, AES-GCM шифрует. Оба — из встроенного
 * в браузер WebCrypto: своей криптографии в приложении нет и быть не должно.
 *
 * - **Соль** случайная у каждой копии. Общая соль позволила бы посчитать
 *   таблицу ключей один раз и открыть ею копии всех людей сразу.
 * - **Вектор** случайный у каждой копии. Повторно использованный вектор в
 *   AES-GCM ломает шифр целиком — это не перестраховка, а требование режима.
 * - **310 000 итераций** — рекомендация OWASP для PBKDF2-SHA256. На телефоне
 *   это доли секунды раз в сутки, а перебор пароля дорожает на порядки.
 * - **AES-GCM проверяет целостность сам**: подменённый или битый файл не
 *   расшифруется, а не выдаст мусор за данные.
 *
 * ## Чего это не защищает
 *
 * Данные на самом устройстве. Они лежат в базе браузера как лежали, и человек
 * с доступом к устройству прочитает их, не зная пароля. Здесь закрыт ровно
 * один путь — файл, уехавший в чужое хранилище.
 */

import type { Backup } from './export-data';

/** Метка формата: по ней зашифрованная копия узнаётся до всякой расшифровки. */
export const ENCRYPTED_FORMAT = 'kairo-encrypted-1';

/** Рекомендация OWASP для PBKDF2-SHA256. */
export const PBKDF2_ITERATIONS = 310_000;

const SALT_BYTES = 16;
const IV_BYTES = 12;

export interface EncryptedBackup {
  format: typeof ENCRYPTED_FORMAT;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string;
  iv: string;
  /**
   * Дата снятия — единственное, что остаётся открытым.
   *
   * Она нужна списку копий: человек должен видеть, за какой день копия, не
   * вводя пароль к каждой. Больше наружу не выносится ничего — ни число
   * записей, ни имя владельца.
   */
  exportedAt: string;
  data: string;
}

/** Копия закрыта паролем. Проверяется до расшифровки — по форме конверта. */
export function isEncryptedBackup(value: unknown): value is EncryptedBackup {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<EncryptedBackup>;

  return (
    candidate.format === ENCRYPTED_FORMAT &&
    typeof candidate.salt === 'string' &&
    typeof candidate.iv === 'string' &&
    typeof candidate.data === 'string'
  );
}

/** Пароль не подошёл — или файл повреждён. Различить эти случаи AES-GCM не даёт. */
export class WrongPasswordError extends Error {
  constructor() {
    super('Пароль не подходит к этой копии');
    this.name = 'WrongPasswordError';
  }
}

export async function encryptBackup(backup: Backup, password: string): Promise<EncryptedBackup> {
  const salt = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(SALT_BYTES)));
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(IV_BYTES)));
  const key = await deriveKey(password, salt, PBKDF2_ITERATIONS);

  const plaintext = new TextEncoder().encode(JSON.stringify(backup));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);

  return {
    format: ENCRYPTED_FORMAT,
    kdf: 'PBKDF2-SHA256',
    iterations: PBKDF2_ITERATIONS,
    salt: toBase64(salt),
    iv: toBase64(iv),
    exportedAt: backup.exportedAt,
    data: toBase64(new Uint8Array(cipher)),
  };
}

/**
 * Расшифровывает копию. Бросает `WrongPasswordError`, если пароль не подошёл.
 *
 * Число итераций берётся из самого файла, а не из константы: копия, снятая
 * год назад, должна открываться и после того, как рекомендация изменится.
 */
export async function decryptBackup(file: EncryptedBackup, password: string): Promise<Backup> {
  const key = await deriveKey(password, fromBase64(file.salt), file.iterations || PBKDF2_ITERATIONS);

  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(file.iv) },
      key,
      fromBase64(file.data),
    );
  } catch {
    // AES-GCM не различает «не тот пароль» и «файл побит»: и там и там не
    // сходится метка целостности. Для человека это один и тот же тупик.
    throw new WrongPasswordError();
  }

  return JSON.parse(new TextDecoder().decode(plaintext)) as Backup;
}

async function deriveKey(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Байты в base64 порциями.
 *
 * Целиком через `String.fromCharCode(...bytes)` нельзя: копия весит мегабайты,
 * а раскрытие миллиона аргументов переполняет стек вызовов. Проявилось бы это
 * не у всех, а только у тех, кто пользуется приложением давно, — то есть у
 * тех, кому копия нужнее всего.
 */
function toBase64(bytes: Uint8Array<ArrayBuffer>): string {
  const CHUNK = 0x8000;
  let binary = '';

  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }

  return btoa(binary);
}

/**
 * base64 в байты.
 *
 * Тип с явным `ArrayBuffer`, а не `ArrayBufferLike`: WebCrypto не принимает
 * буфер, который в теории может оказаться разделяемым между потоками.
 */
function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));

  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
